import { appAlert } from '@/lib/appAlert';
import { getErrorMessage } from '@/lib/functions';
import { useTranslation } from 'react-i18next';
import { useFocusEffect, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOnAppActive } from '@/hooks/useOnAppActive';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Ionicons } from '@expo/vector-icons';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { TabScreenWrapper } from '@/components/TabScreenWrapper';
import { useAuth } from '@/contexts/AuthContext';
import { useNeedsAction } from '@/contexts/NeedsActionContext';
import type { Agreement, AgreementStatus, Profile } from '@/lib/database.types';
import { deleteDraft, fetchProfilesByIds, listAgreements } from '@/lib/agreements';
import { fetchPartyCounts } from '@/lib/multiParty';
import { counterpartyDisplayName, formatAgreementAmount, circleShortName } from '@/lib/format';
import { supabase } from '@/lib/supabase';
import { ErrorText } from '@/components/ErrorText';

/** Επιστρέφει true αν η συμφωνία χρειάζεται ενέργεια από τον συγκεκριμένο χρήστη. */
function needsMyAction(a: Agreement, userId: string, disputedNeedsResponseIds: Set<string>): boolean {
  // signature_pending: αντισυμβαλλόμενος δεν έχει υπογράψει, ή creator δεν έχει υπογράψει offer
  if (a.status === 'pending') {
    if (a.counterparty_id === userId && !a.counterparty_agreed) return true;
    if (a.creator_id === userId && !a.creator_agreed && a.from_offer) return true;
  }
  // cancel_pending: ο άλλος ζήτησε ακύρωση
  if (a.status === 'cancel_requested' && a.cancel_requested_by !== userId) return true;
  // completion_pending: ο άλλος ζήτησε ολοκλήρωση (το status γίνεται 'pending_completion', όχι 'active')
  if (
    a.status === 'pending_completion' &&
    a.completion_requested_by &&
    a.completion_requested_by !== userId
  ) return true;
  // payment_pending: ο άλλος δήλωσε εξόφληση
  if (
    a.status === 'active' &&
    a.payment_declared_by &&
    a.payment_declared_by !== userId &&
    !a.payment_confirmed_at
  ) return true;
  // dispute: μόνο όποιος ΔΕΝ έχει υποβάλει ακόμα τη δική του δήλωση σε ανοιχτή διαφωνία
  // χρειάζεται πραγματικά ενέργεια (ο initiator έχει ήδη υποβάλει, άρα δεν εμφανίζεται).
  if (a.status === 'disputed' && disputedNeedsResponseIds.has(a.id)) return true;
  return false;
}

type Filter = AgreementStatus | 'all' | 'cancelled_group' | 'needs_action';
type SectionHeader = { _header: true; label: string; key: string };
type ListItem = Agreement | SectionHeader;

/** Πεζά + αφαίρεση τόνων για ανεκτική σε τόνους αναζήτηση. */
function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

const CANCELLED_STATUSES: ReadonlyArray<string> = [
  'cancelled',
  'withdrawn',
  'cancelled_mutual',
  'cancelled_unilateral',
  'terminated',
  'disputed',
];

/** Ταιριάζει μια συμφωνία σε ένα φίλτρο — ίδια λογική χρησιμοποιείται και για
 * το φιλτράρισμα της λίστας και για τα counts που κρύβουν τα κενά chips. */
function filterMatches(
  key: Filter,
  a: Agreement,
  uid: string,
  disputedNeedsResponseIds: Set<string>,
): boolean {
  if (key === 'all') return true;
  if (key === 'needs_action') return needsMyAction(a, uid, disputedNeedsResponseIds);
  if (key === 'cancelled_group') return CANCELLED_STATUSES.includes(a.status);
  // Το "Ενεργές" περιλαμβάνει και τις ενδιάμεσες καταστάσεις μιας ενεργής
  // συμφωνίας (εκκρεμές αίτημα ολοκλήρωσης/ακύρωσης, εκπρόθεσμη) — αλλιώς μια
  // συμφωνία σε pending_completion εξαφανίζεται από το tab μέχρι να λυθεί.
  if (key === 'active') return ['active', 'pending_completion', 'cancel_requested', 'overdue'].includes(a.status);
  return a.status === key;
}

export default function AgreementsListScreen() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { breakdown } = useNeedsAction();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const [partyCounts, setPartyCounts] = useState<Record<string, number>>({});
  const [disputedNeedsResponseIds, setDisputedNeedsResponseIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('active');
  const [query, setQuery] = useState('');

  const activeCount = useMemo(() => agreements.filter((a) => a.status === 'active').length, [agreements]);

  // Σειρά με προτεραιότητα στα πιο επείγοντα· "Όλες" μένει πάντα ορατό, τα
  // υπόλοιπα κρύβονται όταν δεν έχουν καμία συμφωνία, ώστε να μη γεμίζει η
  // σειρά με chips που δεν χρειάζονται άμεσα.
  const FILTERS = useMemo(() => {
    const uid = user?.id ?? '';
    const all: { key: Filter; label: string }[] = [
      { key: 'needs_action', label: t('agreements.filter_needsAction') },
      { key: 'active', label: t('agreements.filter_active') },
      { key: 'all', label: t('agreements.filter_all') },
      { key: 'completed', label: t('agreements.filter_completed') },
      { key: 'draft', label: t('agreements.filter_drafts') },
      { key: 'cancelled_group', label: t('agreements.filter_cancelled') },
    ];
    return all.filter(
      (f) => f.key === 'all' || agreements.some((a) => filterMatches(f.key, a, uid, disputedNeedsResponseIds)),
    );
  }, [agreements, user?.id, disputedNeedsResponseIds, t]);

  // Αν το επιλεγμένο φίλτρο αδειάσει και κρυφτεί το chip του, γύρνα σε "Όλες".
  // Αγνοεί όσο φορτώνει ακόμα (loading) — αλλιώς το προεπιλεγμένο "Ενεργές"
  // resetάρεται αμέσως σε "Όλες" επειδή το chip του δεν υπάρχει ακόμα όταν
  // το agreements είναι ακόμα άδειο πριν ολοκληρωθεί το πρώτο φόρτωμα.
  useEffect(() => {
    if (loading) return;
    if (!FILTERS.some((f) => f.key === filter)) setFilter('all');
  }, [FILTERS, filter, loading]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const rows = await listAgreements();
      setAgreements(rows);
      const ids = rows.flatMap((a) => [a.creator_id, a.counterparty_id ?? '']);
      setProfiles(await fetchProfilesByIds(ids));
      setPartyCounts(await fetchPartyCounts(rows.map((a) => a.id)).catch(() => ({})));

      // Διαφωνίες όπου ΔΕΝ έχω ακόμα υποβάλει δήλωση (πραγματικά χρειάζονται ενέργεια από μένα).
      const disputedIds = rows.filter((a) => a.status === 'disputed').map((a) => a.id);
      if (disputedIds.length > 0 && user?.id) {
        try {
          const { data: openDisputes } = await supabase
            .from('disputes')
            .select('id, agreement_id')
            .in('agreement_id', disputedIds)
            .eq('status', 'open') as { data: { id: string; agreement_id: string }[] | null };
          const disputeIds = (openDisputes ?? []).map((d) => d.id);
          const { data: myStatements } = disputeIds.length > 0
            ? await supabase.from('dispute_statements').select('dispute_id').in('dispute_id', disputeIds).eq('user_id', user.id) as { data: { dispute_id: string }[] | null }
            : { data: [] as { dispute_id: string }[] };
          const submittedDisputeIds = new Set((myStatements ?? []).map((s) => s.dispute_id));
          const needsResponse = new Set(
            (openDisputes ?? [])
              .filter((d) => !submittedDisputeIds.has(d.id))
              .map((d) => d.agreement_id),
          );
          setDisputedNeedsResponseIds(needsResponse);
        } catch {
          setDisputedNeedsResponseIds(new Set());
        }
      } else {
        setDisputedNeedsResponseIds(new Set());
      }
    } catch (e) {
      setError(getErrorMessage(e, t('agreements.listLoadFailed')));
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useFocusEffect(useCallback(() => { void load(); }, [load]));
  useOnAppActive(() => void load());

  async function onRefresh() {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }

  const visible = useMemo(() => {
    const uid = user?.id ?? '';
    // Φίλτρο κατάστασης
    const byStatus = agreements.filter((a) => filterMatches(filter, a, uid, disputedNeedsResponseIds));

    // Αναζήτηση
    const q = normalize(query.trim());
    const filtered = q
      ? byStatus.filter((a) => {
          const otherId = a.creator_id === uid ? a.counterparty_id : a.creator_id;
          const otherProfile = otherId ? profiles[otherId] : null;
          const name = otherProfile?.full_name || '';
          const nick = otherProfile?.nickname || '';
          return normalize(a.title).includes(q) || normalize(name).includes(q) || normalize(nick).includes(q);
        })
      : byStatus;

    // Ομαδοποίηση: 3 ομάδες εμφάνισης
    // group 2 = Εκκρεμείς (draft + needs action + pending)
    // group 1 = Ενεργές
    // group 0 = Αρχείο (completed / cancelled / rest)
    function group(a: Agreement): number {
      if (a.status === 'draft' || needsMyAction(a, uid, disputedNeedsResponseIds) || a.status === 'pending' || a.status === 'disputed') return 2;
      if (a.status === 'active') return 1;
      return 0;
    }
    const sorted = [...filtered].sort((a, b) => {
      const gd = group(b) - group(a);
      if (gd !== 0) return gd;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    // Εισάγουμε section headers μόνο στο "all" filter
    if (filter !== 'all' || q) return sorted as ListItem[];
    const result: ListItem[] = [];
    let lastGroup = -1;
    const GROUP_LABELS: Record<number, string> = { 2: t('agreements.groupPending'), 1: t('agreements.groupActive'), 0: t('agreements.groupArchive') };
    for (const a of sorted) {
      const g = group(a);
      if (g !== lastGroup) {
        result.push({ _header: true, label: GROUP_LABELS[g] ?? '', key: `header-${g}` });
        lastGroup = g;
      }
      result.push(a);
    }
    return result;
  }, [agreements, filter, query, profiles, user?.id, disputedNeedsResponseIds]);

  function otherPartyName(a: Agreement): string {
    // Όταν βλέπουμε ως αντισυμβαλλόμενοι, ο «άλλος» είναι πάντα ο δημιουργός
    // και είναι εγγεγραμμένος εξ ορισμού.
    if (a.creator_id !== user?.id) {
      const p = profiles[a.creator_id];
      if (!p?.full_name?.trim()) return t('common.unknownUser');
      return circleShortName(p.full_name, p.nickname);
    }
    // Ως δημιουργοί: αν ο προσκεκλημένος δεν έχει εγγραφεί ακόμα, δείχνουμε
    // το όνομα που δώσαμε εμείς αντί για «Χωρίς αντισυμβαλλόμενο».
    return counterpartyDisplayName(
      a,
      a.counterparty_id
        ? circleShortName(profiles[a.counterparty_id]?.full_name, profiles[a.counterparty_id]?.nickname)
        : null,
      {
        invited: (v) => t('agreementDetail.invitedLabel', { name: v }),
        unknown: t('common.unknownUser'),
        empty: t('agreements.noCounterparty'),
      },
    );
  }

  function roleLabel(a: Agreement): string {
    return a.creator_id === user?.id ? t('agreements.role_with') : t('agreements.role_from');
  }

  function daysUntil(iso: string | null): number | null {
    if (!iso) return null;
    return Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  }

  // Σταθερή αναφορά ώστε το FlatList να μην ξαναφτιάχνει το closure σε κάθε
  // render (π.χ. κάθε πληκτρολόγηση στην αναζήτηση) — προηγουμένως ήταν
  // inline function μέσα στο JSX, ακυρώνοντας το recycling του FlatList.
  const renderItem = useCallback(({ item }: { item: ListItem }) => {
    if ('_header' in item) {
      return (
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionHeaderText}>{(item as SectionHeader).label}</Text>
        </View>
      );
    }
    const actionNeeded = needsMyAction(item, user?.id ?? '', disputedNeedsResponseIds);
    return (
    <Pressable
      style={({ pressed }) => [
        styles.card,
        actionNeeded && styles.cardActionNeeded,
        pressed && styles.cardPressed,
      ]}
      onPress={() => router.push({ pathname: '/(app)/agreements/[id]', params: { id: item.id } })}
      android_ripple={{ color: 'rgba(251,191,36,0.15)' }}
      accessibilityRole="button"
    >
      {/* Accent bar αριστερά για action cards */}
      {actionNeeded && <View style={styles.accentBar} />}

      <View style={styles.cardHeader}>
        <Text style={[styles.cardTitle, actionNeeded && styles.cardTitleAction]} numberOfLines={1}>
          {item.title}
        </Text>
        <View style={styles.cardHeaderRight}>
          {(() => {
            const partyCount = partyCounts[item.id];
            const isMulti = !!partyCount && partyCount > 2;
            return (
              <View style={styles.partyIconWrap}>
                <Ionicons name={isMulti ? 'people' : 'people-outline'} size={14} color={colors.textMuted} />
                {isMulti && <Text style={styles.partyIconCount}>{partyCount}</Text>}
              </View>
            );
          })()}
          <View>
            <StatusBadge status={item.status} muted={!actionNeeded} />
            {actionNeeded && (
              <View style={styles.actionDot}>
                <Text style={styles.actionDotText}>!</Text>
              </View>
            )}
          </View>
          {item.status === 'draft' && (
            <Pressable
              hitSlop={8}
              onPress={(e) => {
                e.stopPropagation();
                appAlert(
                  t('agreements.delete_draft_title'),
                  t('agreements.delete_draft_message'),
                  [
                    { text: t('common.cancel'), style: 'cancel' },
                    {
                      text: t('common.delete'),
                      style: 'destructive',
                      onPress: () => {
                        void deleteDraft(item.id).then(() => load()).catch(() => {});
                      },
                    },
                  ],
                );
              }}
              accessibilityRole="button"
              accessibilityLabel={t('agreements.delete_draft_title')}
            >
              <Ionicons name="trash-outline" size={20} color={colors.danger} />
            </Pressable>
          )}
        </View>
      </View>
      <Text style={styles.cardParty}>
        {roleLabel(item)}: {otherPartyName(item)}
      </Text>
      {item.status === 'draft' && item.send_available_at && (() => {
        const availMs = new Date(item.send_available_at).getTime() - Date.now();
        if (availMs <= 0) return null;
        const hoursLeft = Math.ceil(availMs / 3600000);
        const label = hoursLeft <= 1 ? t('agreements.cooldown_chip') : t('agreements.cooldown_chip_hours', { hours: hoursLeft });
        return (
          <View style={styles.cooldownChip}>
            <Ionicons name="time-outline" size={12} color="#FBBF24" />
            <Text style={styles.cooldownChipText}>{label}</Text>
          </View>
        );
      })()}
      <View style={styles.cardFooter}>
        <View style={styles.cardChips}>
          {/* Ποσό / Ποσοστό */}
          {item.amount != null && item.amount > 0 && (() => {
            const isPercent = item.amount_unit === 'percent';
            return (
              <View style={styles.amountChip}>
                <Ionicons
                  name={isPercent ? 'stats-chart-outline' : 'cash-outline'}
                  size={12}
                  color="#10B981"
                />
                <Text style={styles.amountChipText}>
                  {formatAgreementAmount(item.amount, item.amount_unit, item.currency)}
                </Text>
              </View>
            );
          })()}
          {/* Από προσφορά */}
          {item.from_offer && (
            <View style={styles.fromOfferChip}>
              <Ionicons name="pricetag-outline" size={11} color={colors.primaryLight} />
              <Text style={styles.fromOfferChipText}>{t('agreements.fromOfferChip')}</Text>
            </View>
          )}
          {/* Λήξη */}
          {item.expires_at && (() => {
            const diff = Math.ceil((new Date(item.expires_at).getTime() - Date.now()) / 86400000);
            const expired = diff < 0;
            const urgent = !expired && diff <= 7;
            const color = expired ? '#EF4444' : urgent ? '#F97316' : colors.textMuted;
            const borderColor = expired ? '#EF444440' : urgent ? '#F9731630' : colors.border;
            const bg = expired ? '#EF444412' : urgent ? '#F9731612' : 'transparent';
            const label = expired
              ? t('common.expired')
              : urgent
                ? `${diff === 0 ? t('common.today') : t('agreements.daysAbbrev', { count: diff })} ⏰`
                : new Date(item.expires_at).toLocaleDateString('el-GR', { day: 'numeric', month: 'short' });
            return (
              <View style={[styles.expiryChip, { borderColor, backgroundColor: bg }]}>
                {(expired || urgent) && (
                  <Ionicons name="time-outline" size={12} color={color} />
                )}
                <Text style={[styles.expiryChipText, { color }]}>{label}</Text>
              </View>
            );
          })()}
        </View>
      </View>
    </Pressable>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styles, colors, t, partyCounts, disputedNeedsResponseIds, user?.id, router, load, profiles]);

  return (
    <TabScreenWrapper>
    <View style={styles.outer}>
    <View style={[styles.container, { paddingTop: insets.top + spacing.lg }]}>
      <View style={styles.topBar}>
        <Text style={styles.title}>{t('agreements.title')}</Text>
      </View>

      <View style={styles.searchWrap}>
        <Text style={styles.searchIcon}>🔍</Text>
        <TextInput
          style={styles.searchInput}
          value={query}
          onChangeText={setQuery}
          placeholder={t('agreements.search_placeholder')}
          placeholderTextColor={colors.textMuted}
          returnKeyType="search"
          autoCorrect={false}
        />
        {query.length > 0 && (
          <Pressable onPress={() => setQuery('')} hitSlop={8} accessibilityRole="button">
            <Text style={styles.searchClear}>✕</Text>
          </Pressable>
        )}
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.chips}
        style={styles.chipsRow}
        keyboardShouldPersistTaps="handled"
      >
        {FILTERS.map((f) => {
          const selected = filter === f.key;
          const glowing = f.key === 'needs_action' && breakdown.agreements > 0;
          return (
            <Pressable
              key={f.key}
              onPress={() => setFilter(f.key)}
              style={[styles.chip, selected && styles.chipSelected, glowing && styles.chipGlow]}
              accessibilityRole="button"
            >
              <Text style={[styles.chipText, selected && styles.chipTextSelected]}>
                {f.label}
                {f.key === 'active' && activeCount > 0 && (
                  <Text style={styles.chipCountActive}> {activeCount}</Text>
                )}
              </Text>
              {f.key === 'needs_action' && <Badge count={breakdown.agreements} style={styles.chipBadge} />}
            </Pressable>
          );
        })}
      </ScrollView>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={visible}
          keyExtractor={(a) => ('_header' in a ? (a as SectionHeader).key : a.id)}
          style={styles.flatList}
          contentContainerStyle={styles.list}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={colors.primary}
              colors={[colors.primary]}
            />
          }
          ListEmptyComponent={
            <View style={styles.center}>
              <Text style={styles.emptyTitle}>
                {query.trim()
                  ? t('agreements.empty_noResults')
                  : filter === 'all'
                    ? t('agreements.empty_noAgreements')
                    : filter === 'needs_action'
                      ? t('agreements.empty_allGood')
                      : t('agreements.empty_noneHere')}
              </Text>
              <Text style={styles.emptyText}>
                {query.trim()
                  ? t('agreements.emptySearchBody', { query: query.trim() })
                  : filter === 'all'
                    ? t('agreements.emptyAllBody')
                    : filter === 'needs_action'
                      ? t('agreements.emptyNeedsActionBody')
                      : t('agreements.emptyOtherBody')}
              </Text>
            </View>
          }
          renderItem={renderItem}
        />
      )}

      <ErrorText>{error}</ErrorText>

      <View style={[styles.footer, { paddingBottom: spacing.sm }]}>
        <Button
          title={t('agreements.new')}
          icon="add"
          onPress={() => router.push('/(app)/agreements/new')}
        />
      </View>
    </View>
    </View>
    </TabScreenWrapper>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    outer: {
      flex: 1,
      backgroundColor: colors.background,
    },
    container: {
      flex: 1,
      paddingHorizontal: spacing.lg,
    },
    topBar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: spacing.md,
    },
    back: {
      color: colors.primaryLight,
      fontSize: fontSize.md,
      fontWeight: '600',
      width: 70,
    },
    title: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
    },
    spacer: { width: 70 },
    searchWrap: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      backgroundColor: colors.surface,
      borderRadius: radius.full,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: spacing.md,
      paddingVertical: 6,
      marginBottom: spacing.sm,
    },
    searchIcon: {
      fontSize: fontSize.sm,
    },
    searchInput: {
      flex: 1,
      color: colors.text,
      fontSize: fontSize.md,
      paddingVertical: 2,
    },
    searchClear: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      fontWeight: '700',
    },
    chipsRow: {
      flexGrow: 0,
      flexShrink: 0,
      backgroundColor: colors.background,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      marginBottom: spacing.sm,
    },
    chips: {
      gap: spacing.sm,
      paddingVertical: spacing.sm,
    },
    flatList: {
      flex: 1,
    },
    chip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.full,
      paddingVertical: 6,
      paddingHorizontal: spacing.md,
    },
    chipSelected: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    chipGlow: {
      borderColor: '#F59E0B',
      shadowColor: '#F59E0B',
      shadowOpacity: 0.9,
      shadowRadius: 8,
      shadowOffset: { width: 0, height: 0 },
      elevation: 6,
    },
    chipText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    chipTextSelected: {
      color: colors.textOnPrimary,
    },
    chipCountActive: {
      color: colors.success,
      fontWeight: '800',
    },
    chipBadge: {
      marginLeft: 2,
    },
    list: {
      gap: spacing.md,
      paddingVertical: spacing.sm,
      flexGrow: 1,
    },
    sectionHeader: {
      paddingHorizontal: spacing.xs,
      paddingBottom: 2,
      marginBottom: -spacing.xs,
    },
    sectionHeaderText: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.8,
    },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.xs,
      overflow: 'hidden',
      ...shadow.card,
    },
    cardActionNeeded: {
      borderColor: 'rgba(201,168,67,0.70)',
      borderWidth: 1.5,
      boxShadow: '0px 0px 14px rgba(201,168,67,0.28)',
      elevation: 8,
    },
    accentBar: {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: 3,
      backgroundColor: 'rgba(251,191,36,0.6)',
      borderTopLeftRadius: radius.lg,
      borderBottomLeftRadius: radius.lg,
    },
    actionDot: {
      position: 'absolute',
      bottom: -4,
      right: -4,
      width: 14,
      height: 14,
      borderRadius: 7,
      backgroundColor: '#F97316',
      alignItems: 'center',
      justifyContent: 'center',
    },
    actionDotText: {
      color: '#FFFFFF',
      fontSize: 9,
      fontWeight: '800',
      lineHeight: 14,
    },
    cardTitleAction: {
      color: colors.text,
      fontWeight: '700',
    },
    cardPressed: { opacity: 0.85 },
    cardHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: spacing.sm,
    },
    cardTitle: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '600',
      flex: 1,
    },
    cardHeaderRight: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },
    partyIconWrap: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2,
    },
    partyIconCount: {
      color: colors.textMuted,
      fontSize: 11,
      fontWeight: '700',
    },
    cardParty: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    cardFooter: {
      marginTop: spacing.xs,
    },
    cardChips: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 6,
    },
    amountChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      borderRadius: radius.full,
      borderWidth: 1,
      borderColor: '#10B98130',
      backgroundColor: '#10B98112',
      paddingVertical: 3,
      paddingHorizontal: spacing.sm,
    },
    amountChipText: {
      color: '#10B981',
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    fromOfferChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      borderRadius: radius.full,
      borderWidth: 1,
      borderColor: `${colors.primaryLight}40`,
      backgroundColor: `${colors.primaryLight}12`,
      paddingVertical: 3,
      paddingHorizontal: spacing.sm,
    },
    fromOfferChipText: {
      color: colors.primaryLight,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    expiryChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      borderRadius: radius.full,
      borderWidth: 1,
      borderColor: colors.border,
      paddingVertical: 3,
      paddingHorizontal: spacing.sm,
    },
    expiryChipText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    cooldownChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      alignSelf: 'flex-start',
      borderRadius: radius.full,
      borderWidth: 1,
      borderColor: 'rgba(251,191,36,0.35)',
      backgroundColor: 'rgba(251,191,36,0.10)',
      paddingVertical: 3,
      paddingHorizontal: spacing.sm,
      marginTop: 4,
    },
    cooldownChipText: {
      color: '#FBBF24',
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.sm,
      paddingVertical: spacing.xxl,
    },
    emptyTitle: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '600',
    },
    emptyText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      textAlign: 'center',
    },
    error: {
      color: colors.danger,
      fontSize: fontSize.sm,
      textAlign: 'center',
      paddingVertical: spacing.sm,
    },
    footer: {
      paddingTop: spacing.md,
    },
  });
}
